import { useSelf } from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { taskModule } from "@rentiohq/shared-frontend/dist/reduxV2/task";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import {
  ETaskMemberType,
  ITask,
} from "@rentiohq/shared-frontend/dist/types/task.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getMembersWithRole } from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import { SHOW_CONFIRM_MODAL } from "@rentiohq/web-shared-next/dist/redux/actions";
import {
  Box,
  ContactSelectInline,
  Stack,
  Typography,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import { useDispatch } from "react-redux";

interface IProps {
  task: ITask;
  title: string;
  placeholder: string;
  role: ETaskMemberType;
}

export const TaskMembers = ({ task, title, placeholder, role }: IProps) => {
  const membersForRole = getMembersWithRole(task.members, role);

  const dispatch = useDispatch();

  const { user } = useSelf();

  const { updateMembers } = taskModule.hooks.useUpdate({
    id: task.id,
  });

  const isOwnAccount = (accountId: number) =>
    user?.accountIds.includes(accountId);

  const handleAddMember = (_?: number[], newAccountId?: number) => {
    if (!newAccountId) {
      return;
    }

    updateMembers({
      membersToAdd: [
        {
          roles: [role],
          accountId: newAccountId,
        },
      ],
      membersToRemove: [],
    });
  };

  const removeMember = (accountId: number) => {
    updateMembers({
      membersToAdd: [],
      membersToRemove: [
        {
          roles: [role],
          accountId,
        },
      ],
    });
  };

  const handleCloseConfirmModal = () => {
    dispatch({
      type: SHOW_CONFIRM_MODAL,
      payload: { showConfirmModal: false },
    });
  };

  const handleRemoveMember = (contact?: IContact) => {
    if (!contact) {
      return;
    }

    const isExecutorRemoval = role === ETaskMemberType.Executor;

    // A task must keep at least one executor, and the API rejects this
    // removal with a 409. Adding a follower does not help here, so the modal
    // carries the API's own message instead of a "remove" that cannot succeed.
    if (isExecutorRemoval && membersForRole.length <= 1) {
      dispatch({
        type: SHOW_CONFIRM_MODAL,
        payload: {
          title: getLocalizedText("tasks.remove.executor"),
          description: getLocalizedText("task.member.delete.last_executor"),
          showConfirmModal: true,
          actions: [
            {
              label: getLocalizedText("system.ok"),
              onClick: handleCloseConfirmModal,
              variant: EButtonVariants.Outlined,
            },
          ],
        },
      });

      return;
    }

    if (isExecutorRemoval && isOwnAccount(contact.accountId)) {
      dispatch({
        type: SHOW_CONFIRM_MODAL,
        payload: {
          title: getLocalizedText("tasks.remove_self_executor.confirm.title"),
          description: getLocalizedText(
            "tasks.remove_self_executor.confirm.body",
          ),
          showConfirmModal: true,
          actions: [
            {
              label: getLocalizedText("system.cancel"),
              onClick: handleCloseConfirmModal,
              variant: EButtonVariants.Outlined,
            },
            {
              label: getLocalizedText("system.remove"),
              onClick: () => {
                removeMember(contact.accountId);
                handleCloseConfirmModal();
              },
            },
          ],
        },
      });

      return;
    }

    removeMember(contact.accountId);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="h5" mb={2}>
          {title}
        </Typography>
      </Stack>
      <ContactSelectInline
        placeholder={placeholder}
        includeSelf={true}
        value={membersForRole.map(member => member.account.id)}
        onChange={handleAddMember}
        handleContactRemove={handleRemoveMember}
      />
    </Box>
  );
};
